import classNames from 'classnames'

import { CopyWithText, ImageComponent } from '@apps-common/components/common'
import { ButtonRoundSm } from '@apps-common/components/elements'
import { VertSpacerLg } from '@apps-common/components/layout'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { UNIT_STATUS_ACTIVE, UNIT_STATUS_CANCELLED, UNIT_STATUS_INACTIVE } from '@apps-common/config/unit-filter'
import { checkAccountNo } from '@apps-common/helpers/unit-helpers'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { IOrderUnitModel } from '@apps-common/types/portal/order'
import { IPortalUnitSearchModel } from '@apps-common/types/portal/unit'
import { ROUTE_UNIT_MANAGE } from '@apps-radionet-portal-root/src/config/private-routes'

interface IProps {
  index: number
  loading: boolean
  updating: boolean
  unit: IOrderUnitModel | IPortalUnitSearchModel
  removeCb?: (unitId: string) => void
}

export const OrderUnitCard: React.FC<IProps> = ({ index, loading, updating, unit, removeCb }) => {
  const { status, unitId, accountNo, productImageUrl, serialNo, imei, mobileNo } = unit
  const simChanged = 'simChanged' in unit ? unit.simChanged : false
  const { navigateForward } = useNavigation()

  return (
    <div key={`order-unit-${unitId}-${index}`} className='relative bg-surface text-text border border-border rounded-3xl p-[20px]'>
      <div className='flex items-center justify-between gap-[20px]'>
        <div className='flex flex-col gap-[10px]'>
          <div className=' flex items-center justify-start gap-[5px]'>
            <div
              className={classNames(
                'absolute top-[12px] inline-block capitalize rounded-full text-[0.75rem] leading-[0.7rem] jost-medium px-[7px] py-[3px]',
                status === UNIT_STATUS_INACTIVE.value && `bg-info text-on-info`,
                status === UNIT_STATUS_ACTIVE.value && `bg-success text-on-success`,
                status === UNIT_STATUS_CANCELLED.value && `bg-warning text-on-warning`
              )}
            >
              {status}
            </div>
            {simChanged && (
              <div className='flex items-center justify-center gap-[5px] bg-warning text-on-warning rounded-full jost-medium text-[0.8rem] leading-[0.8rem] px-[10px] py-[5px]'>
                sim <commonIcons.warning className='text-[0.8rem]' />
              </div>
            )}
          </div>
          <div className='flex items-center justify-start gap-[5px]'>
            <div className='px-4 py-2 h-[20px] rounded-[15px] flex items-center justify-start gap-[10px] min-w-0 bg-surface-2'>
              <commonIcons.processor className='text-text' />
              <div className={classNames('jost-light text-[0.85rem] uppercase')}>{checkAccountNo(accountNo, status)}</div>
            </div>
          </div>
        </div>
        <div
          className={classNames(
            'w-[45px] h-[45px] rounded-full flex items-center justify-center overflow-hidden bg-surface-2 border border-border'
          )}
        >
          <ImageComponent
            src={productImageUrl}
            alt={`Product ${accountNo}`}
            imageClasses={classNames('max-w-[30px]')}
            errorIconClasses='text-[1.2rem]'
          />
        </div>
      </div>
      <VertSpacerLg />
      <div className='flex items-end justify-between gap-[5px]'>
        <div className='grow'>
          {status === UNIT_STATUS_INACTIVE.value && (
            <>
              <CopyWithText textValue='SN' value={serialNo} showCopyValue className={classNames('!text-[0.75rem]')} />
              <CopyWithText textValue='IMEI' value={imei} showCopyValue className={classNames('!text-[0.75rem]')} />
            </>
          )}
          {status === UNIT_STATUS_ACTIVE.value && (
            <>
              <CopyWithText textValue='MOB' value={mobileNo} showCopyValue className={classNames('grow !text-[0.75rem]')} />
              <CopyWithText textValue='IMEI' value={imei} showCopyValue className={classNames('!text-[0.75rem]')} />
            </>
          )}
        </div>
        <div className='flex items-center justify-end gap-[10px]'>
          {removeCb && (
            <ModalButton
              component={ConfirmModal}
              disabled={false}
              classes=''
              componentProps={{
                text: 'Remove Device',
                description: 'Confirm you would like to remove the device from this order?',
                action: ConfirmModalAction.Error
              }}
              confirmCallback={() => removeCb(unit.unitId)}
            >
              <ButtonRoundSm
                type='div'
                className='action-warning'
                disabled={loading || updating}
                tooltip={{
                  id: `manage-order-unit-removal-${unitId}-${index}`,
                  message: 'Remove device'
                }}
              >
                <commonIcons.delete />
              </ButtonRoundSm>
            </ModalButton>
          )}
          <ButtonRoundSm
            type='button'
            className='action-brand-solid'
            disabled={loading || updating}
            tooltip={{
              id: `manage-order-unit-details-${unitId}-${index}`,
              message: 'Manage device'
            }}
            callback={() => {
              if (!loading && !updating) {
                navigateForward({ routeUrl: ROUTE_UNIT_MANAGE.buildPath(status, unitId), route: ROUTE_UNIT_MANAGE })
              }
            }}
          >
            <commonIcons.arrowAngled />
          </ButtonRoundSm>
        </div>
      </div>
    </div>
  )
}
